/**
 * ExpressionPreview - Reusable component for showing evaluated expression previews
 *
 * Shows a toggle button when expression content is detected. Clicking the button
 * opens the preview panel, which evaluates expressions in an isolated Web Worker.
 *
 * SECURITY: All evaluation happens in a Worker with no DOM/storage/network access.
 * The preview is user-triggered (not auto-shown on focus) to minimize attack surface.
 */

import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import {
  makeStyles,
  tokens,
  shorthands,
  mergeClasses,
  Text,
  Button,
  ToggleButton,
  Tooltip,
  Spinner,
} from '@fluentui/react-components';
import { InfoRegular, CodeRegular, EyeRegular, EyeOffRegular } from '@fluentui/react-icons';
import { LOOP_FUNCTION_PATTERN, type EvaluationContext } from '@/utils/expressions';
import { evaluateBatchAsync } from '@/utils/expressions/workerEvaluator';
import { formatResolvedValue } from '@/utils/expressions';
import { customScrollbarStyles } from '@/styles/scrollbar';
import { ottoTokens } from '@/theme/ottoTokens';
import { useParameterValues } from '@/hooks/useParameterValues';
import { sanitizeHtml } from '@/utils/sanitization';

const useStyles = makeStyles({
  // Inline container (default) - for composite fields like DictionaryField
  container: {
    marginTop: tokens.spacingVerticalS,
  },
  // Overlay container - positioned absolutely below the parent, like a dropdown
  containerOverlay: {
    position: 'absolute',
    top: '100%',
    left: 0,
    right: 0,
    marginTop: tokens.spacingVerticalXXS,
    zIndex: 1000,
  },
  // Panel styling - shared between inline and overlay modes
  // Elevated card: soft fill + shadow instead of a hard border so it lifts off the form.
  panel: {
    paddingTop: tokens.spacingVerticalS,
    paddingBottom: tokens.spacingVerticalS,
    paddingLeft: tokens.spacingHorizontalM,
    paddingRight: tokens.spacingHorizontalM,
    backgroundColor: tokens.colorNeutralBackground1,
    // Concentric radius: outer = inner radius (Medium = 4px) + horizontal padding (~12px)
    // — Large (6px) gives just enough rounding to read as a card.
    borderRadius: tokens.borderRadiusLarge,
    boxShadow: tokens.shadow4,
    // Forced-colors strips box-shadow entirely, so re-draw the card edge with a system colour.
    '@media (forced-colors: active)': {
      ...shorthands.border('1px', 'solid', 'CanvasText'),
    },
  },
  // Overlay panel has stronger shadow for depth above other content
  panelOverlay: {
    boxShadow: tokens.shadow8,
  },
  header: {
    display: 'flex',
    alignItems: 'center',
    gap: tokens.spacingHorizontalXS,
    marginBottom: tokens.spacingVerticalXS,
  },
  headerWithActions: {
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'space-between',
    marginBottom: tokens.spacingVerticalXS,
  },
  // Removes the header’s bottom margin when the body is hidden so the
  // collapsed panel reads as a single tight bar instead of a chunky pill.
  headerCollapsed: {
    marginBottom: 0,
  },
  headerActions: {
    display: 'flex',
    alignItems: 'center',
    gap: tokens.spacingHorizontalXXS,
  },
  // "PREVIEW" eyebrow: small, tracked-out, mono — reads as a section marker, not body text.
  label: {
    fontFamily: tokens.fontFamilyMonospace,
    fontSize: tokens.fontSizeBase100,
    fontWeight: tokens.fontWeightSemibold,
    color: tokens.colorNeutralForeground3,
    textTransform: 'uppercase',
    letterSpacing: '0.08em',
  },
  // Inset code block — concentric: outer radius (Large, 6px) - padding (~8px) → inner radius Medium (4px).
  codeBlock: {
    backgroundColor: tokens.colorNeutralBackground2,
    borderRadius: tokens.borderRadiusMedium,
    paddingTop: tokens.spacingVerticalS,
    paddingBottom: tokens.spacingVerticalS,
    paddingLeft: tokens.spacingHorizontalS,
    paddingRight: tokens.spacingHorizontalS,
    '@media (forced-colors: active)': {
      ...shorthands.border('1px', 'solid', 'CanvasText'),
    },
  },
  content: {
    fontFamily: tokens.fontFamilyMonospace,
    fontSize: tokens.fontSizeBase200,
    lineHeight: tokens.lineHeightBase200,
    fontVariantNumeric: 'tabular-nums',
    color: tokens.colorNeutralForeground1,
    whiteSpace: 'pre-wrap',
    wordBreak: 'break-word',
    maxHeight: '200px',
    overflowY: 'auto',
    overflowX: 'hidden',
    userSelect: 'text',
    cursor: 'text',
    ...customScrollbarStyles,
  },
  // JSON syntax token classes — applied to literal segments when the value is a JSON object/array.
  jsonKey: {
    color: ottoTokens.codeSyntaxPropertyName,
  },
  jsonString: {
    color: ottoTokens.codeSyntaxString,
  },
  jsonNumber: {
    color: ottoTokens.codeSyntaxNumber,
  },
  jsonBoolean: {
    color: ottoTokens.codeSyntaxBoolean,
  },
  jsonNull: {
    color: ottoTokens.codeSyntaxNull,
  },
  jsonPunctuation: {
    color: ottoTokens.codeSyntaxPunctuation,
  },
  // Resolved expression value styling — soft tint, not a hard fill.
  resolvedValue: {
    backgroundColor: tokens.colorPaletteGreenBackground1,
    color: tokens.colorPaletteGreenForeground1,
    borderRadius: tokens.borderRadiusSmall,
    paddingLeft: tokens.spacingHorizontalXXS,
    paddingRight: tokens.spacingHorizontalXXS,
    fontFamily: tokens.fontFamilyMonospace,
    fontSize: tokens.fontSizeBase200,
    // Forced-colors strips background-color; use system Mark/MarkText for the highlight cue.
    '@media (forced-colors: active)': {
      backgroundColor: 'Mark',
      color: 'MarkText',
      forcedColorAdjust: 'none',
    },
  },
  placeholder: {
    color: tokens.colorNeutralForeground4,
    fontStyle: 'italic',
    textDecorationLine: 'underline',
    textDecorationStyle: 'dotted',
    textDecorationColor: tokens.colorNeutralStroke2,
    textUnderlineOffset: '2px',
    '@media (forced-colors: active)': {
      color: 'GrayText',
    },
  },
  error: {
    color: tokens.colorPaletteRedForeground1,
    fontStyle: 'italic',
    '@media (forced-colors: active)': {
      color: 'LinkText',
    },
  },
  // Tip footer sits in its own band, separated by a hairline that survives HCM.
  tip: {
    marginTop: tokens.spacingVerticalS,
    paddingTop: tokens.spacingVerticalS,
    fontSize: tokens.fontSizeBase200,
    lineHeight: tokens.lineHeightBase200,
    color: tokens.colorNeutralForeground3,
    display: 'flex',
    alignItems: 'flex-start',
    gap: tokens.spacingHorizontalXS,
    textWrap: 'pretty',
    boxShadow: `inset 0 1px 0 0 ${tokens.colorNeutralStroke2}`,
    '@media (forced-colors: active)': {
      boxShadow: 'none',
      borderTop: '1px solid CanvasText',
    },
  },
  // Optical centering: the InfoRegular glyph reads slightly high; nudge down 1px so it
  // visually aligns with the cap-height baseline of the text next to it.
  tipIcon: {
    color: tokens.colorPaletteYellowForeground1,
    flexShrink: 0,
    marginTop: '1px',
    '@media (forced-colors: active)': {
      color: 'CanvasText',
    },
  },
  tipText: {
    textWrap: 'pretty',
  },
  // HTML preview styles
  htmlPreviewContainer: {
    backgroundColor: tokens.colorNeutralBackground2,
    borderRadius: tokens.borderRadiusMedium,
    padding: tokens.spacingVerticalS,
    maxHeight: '300px',
    overflowY: 'auto',
    overflowX: 'hidden',
    ...customScrollbarStyles,
    // Reset some styles for proper HTML rendering
    '& h1, & h2, & h3, & h4, & h5, & h6': {
      marginTop: tokens.spacingVerticalS,
      marginBottom: tokens.spacingVerticalXS,
      color: tokens.colorNeutralForeground1,
    },
    '& h1': { fontSize: tokens.fontSizeBase600 },
    '& h2': { fontSize: tokens.fontSizeBase500 },
    '& h3': { fontSize: tokens.fontSizeBase400 },
    '& p': {
      marginTop: tokens.spacingVerticalXS,
      marginBottom: tokens.spacingVerticalXS,
      color: tokens.colorNeutralForeground1,
    },
    '& strong': { fontWeight: tokens.fontWeightSemibold },
    '& em': { fontStyle: 'italic' },
    '& ul, & ol': {
      paddingLeft: tokens.spacingHorizontalL,
      marginTop: tokens.spacingVerticalXS,
      marginBottom: tokens.spacingVerticalXS,
    },
    '& li': { marginBottom: tokens.spacingVerticalXXS },
    '& a': { color: tokens.colorBrandForeground1 },
    '& table': {
      borderCollapse: 'collapse',
      width: '100%',
      marginTop: tokens.spacingVerticalS,
      marginBottom: tokens.spacingVerticalS,
    },
    '& th, & td': {
      ...shorthands.border('1px', 'solid', tokens.colorNeutralStroke1),
      padding: tokens.spacingVerticalXS,
      textAlign: 'left',
    },
    '& th': {
      backgroundColor: tokens.colorNeutralBackground3,
      fontWeight: tokens.fontWeightSemibold,
    },
    // Resolved expression values in HTML preview
    '& .resolved-value': {
      backgroundColor: tokens.colorPaletteGreenBackground1,
      color: tokens.colorPaletteGreenForeground1,
      borderRadius: tokens.borderRadiusSmall,
      paddingLeft: tokens.spacingHorizontalXXS,
      paddingRight: tokens.spacingHorizontalXXS,
      fontFamily: tokens.fontFamilyMonospace,
      fontSize: tokens.fontSizeBase200,
      '@media (forced-colors: active)': {
        backgroundColor: 'Mark',
        color: 'MarkText',
        forcedColorAdjust: 'none',
      },
    },
    // Unresolved expression placeholders in HTML preview
    '& .unresolved-value': {
      color: tokens.colorNeutralForeground4,
      fontStyle: 'italic',
      '@media (forced-colors: active)': {
        color: 'GrayText',
      },
    },
    // Error values in HTML preview
    '& .error-value': {
      color: tokens.colorPaletteRedForeground1,
      fontStyle: 'italic',
      '@media (forced-colors: active)': {
        color: 'LinkText',
      },
    },
  },
  toggleButton: {
    minWidth: 'auto',
    padding: `${tokens.spacingVerticalXXS} ${tokens.spacingHorizontalS}`,
  },
  previewToggleButton: {
    minWidth: 'auto',
    fontSize: tokens.fontSizeBase200,
    color: tokens.colorNeutralForeground3,
    padding: `${tokens.spacingVerticalXXS} ${tokens.spacingHorizontalS}`,
  },
  loadingContainer: {
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    paddingTop: tokens.spacingVerticalS,
    paddingBottom: tokens.spacingVerticalS,
  },
});

/** Segment type for preview rendering */
interface PreviewSegment {
  text: string;
  type: 'literal' | 'resolved' | 'unresolved' | 'error';
}

/** Token type for JSON syntax highlighting of literal segments */
type JsonTokenKind = 'key' | 'string' | 'number' | 'boolean' | 'null' | 'punctuation' | 'plain';

interface JsonToken {
  kind: JsonTokenKind;
  text: string;
}

/**
 * Lightweight JSON tokenizer used to colourise stringified-object previews.
 * Operates on already-formatted output from JSON.stringify(value, null, 2),
 * so input is well-formed; we only need to split it for syntax colours.
 */
function tokenizeJson(text: string): JsonToken[] {
  const result: JsonToken[] = [];
  let i = 0;
  const len = text.length;

  while (i < len) {
    const ch = text.charAt(i);

    // Whitespace (incl. newlines for pretty-printed JSON)
    if (ch === ' ' || ch === '\t' || ch === '\n' || ch === '\r') {
      let j = i + 1;
      while (j < len) {
        const c = text.charAt(j);
        if (c !== ' ' && c !== '\t' && c !== '\n' && c !== '\r') break;
        j++;
      }
      result.push({ kind: 'plain', text: text.substring(i, j) });
      i = j;
      continue;
    }

    // String — could be a key (followed by ':') or a value
    if (ch === '"') {
      let j = i + 1;
      while (j < len) {
        const c = text.charAt(j);
        if (c === '\\' && j + 1 < len) {
          j += 2;
          continue;
        }
        if (c === '"') {
          j++;
          break;
        }
        j++;
      }
      const str = text.substring(i, j);
      // Look ahead past whitespace to determine if this string is a key.
      let k = j;
      while (k < len) {
        const c = text.charAt(k);
        if (c !== ' ' && c !== '\t' && c !== '\n' && c !== '\r') break;
        k++;
      }
      const isKey = text.charAt(k) === ':';
      result.push({ kind: isKey ? 'key' : 'string', text: str });
      i = j;
      continue;
    }

    // Number
    if (ch === '-' || (ch >= '0' && ch <= '9')) {
      const remainder = text.substring(i);
      const match = remainder.match(/^-?\d+(\.\d+)?([eE][+-]?\d+)?/);
      if (match) {
        result.push({ kind: 'number', text: match[0] });
        i += match[0].length;
        continue;
      }
    }

    // Keywords
    if (text.startsWith('true', i)) {
      result.push({ kind: 'boolean', text: 'true' });
      i += 4;
      continue;
    }
    if (text.startsWith('false', i)) {
      result.push({ kind: 'boolean', text: 'false' });
      i += 5;
      continue;
    }
    if (text.startsWith('null', i)) {
      result.push({ kind: 'null', text: 'null' });
      i += 4;
      continue;
    }

    // Structural punctuation
    if (ch === '{' || ch === '}' || ch === '[' || ch === ']' || ch === ',' || ch === ':') {
      result.push({ kind: 'punctuation', text: ch });
      i++;
      continue;
    }

    // Anything else — pass through unstyled (defensive: tokenizer should never block rendering).
    result.push({ kind: 'plain', text: ch });
    i++;
  }

  return result;
}

/**
 * Check if text contains any expression patterns
 */
function containsExpression(text: string): boolean {
  return /\$\{[^}]+\}|@\{[^}]+\}|@[a-zA-Z_]+\(/.test(text);
}

/**
 * Check if text contains JavaScript expression patterns (${...})
 */
function containsJavaScriptExpression(text: string): boolean {
  return /\$\{[^}]+\}/.test(text);
}

/**
 * Check if text contains HTML tags
 * Looks for common HTML patterns like <tag>, <tag attr="value">, </tag>, <tag/>
 */
function containsHtmlTags(text: string): boolean {
  // Match opening tags, closing tags, and self-closing tags
  // Common HTML tags: div, span, p, h1-h6, strong, em, a, ul, ol, li, table, tr, td, th, br, hr, img, etc.
  const htmlTagPattern = /<\/?(?:div|span|p|h[1-6]|strong|em|b|i|u|a|ul|ol|li|table|thead|tbody|tr|td|th|br|hr|img|pre|code|blockquote|section|article|header|footer|nav|main|aside|figure|figcaption|form|input|button|label|select|option|textarea)(?:\s[^>]*)?\/?>/i;
  return htmlTagPattern.test(text);
}

/**
 * Parse text into segments (literal text and expressions)
 */
function parseExpressions(text: string): Array<{ text: string; isExpression: boolean }> {
  if (!text) return [];

  const result: Array<{ text: string; isExpression: boolean }> = [];
  let pos = 0;
  let lastEnd = 0;

  while (pos < text.length) {
    // Check for ${...} expression
    if (pos < text.length - 1 && text.charAt(pos) === '$' && text.charAt(pos + 1) === '{') {
      const end = findExpressionEnd(text, pos, '${');
      if (end > 0) {
        if (pos > lastEnd) {
          result.push({ text: text.substring(lastEnd, pos), isExpression: false });
        }
        result.push({ text: text.substring(pos, end), isExpression: true });
        lastEnd = end;
        pos = end;
        continue;
      }
    }

    // Check for @{...} expression
    if (pos < text.length - 1 && text.charAt(pos) === '@' && text.charAt(pos + 1) === '{') {
      const end = findExpressionEnd(text, pos, '@{');
      if (end > 0) {
        if (pos > lastEnd) {
          result.push({ text: text.substring(lastEnd, pos), isExpression: false });
        }
        result.push({ text: text.substring(pos, end), isExpression: true });
        lastEnd = end;
        pos = end;
        continue;
      }
    }

    // Check for @function(...) expression
    if (text.charAt(pos) === '@' && pos + 1 < text.length && /[a-zA-Z]/.test(text.charAt(pos + 1))) {
      const end = findInlineFunctionEnd(text, pos);
      if (end > pos) {
        if (pos > lastEnd) {
          result.push({ text: text.substring(lastEnd, pos), isExpression: false });
        }
        result.push({ text: text.substring(pos, end), isExpression: true });
        lastEnd = end;
        pos = end;
        continue;
      }
    }

    pos++;
  }

  if (lastEnd < text.length) {
    result.push({ text: text.substring(lastEnd), isExpression: false });
  }

  return result;
}

/**
 * Find the end of a brace-delimited expression
 */
function findExpressionEnd(text: string, start: number, opener: string): number {
  let pos = start + opener.length;
  let depth = 1;
  let inString = false;
  let stringChar = '';

  while (pos < text.length && depth > 0) {
    const char = text.charAt(pos);

    if (inString) {
      if (char === stringChar && text.charAt(pos - 1) !== '\\') {
        inString = false;
      }
    } else {
      if (char === '"' || char === "'") {
        inString = true;
        stringChar = char;
      } else if (char === '{') {
        depth++;
      } else if (char === '}') {
        depth--;
      }
    }
    pos++;
  }

  return depth === 0 ? pos : -1;
}

/**
 * Find the end of an inline @function(...) expression
 */
function findInlineFunctionEnd(text: string, start: number): number {
  let pos = start + 1;

  while (pos < text.length && /[a-zA-Z_]/.test(text.charAt(pos))) {
    pos++;
  }

  if (pos >= text.length || text.charAt(pos) !== '(') {
    return -1;
  }

  let depth = 1;
  pos++;
  let inString = false;
  let stringChar = '';

  while (pos < text.length && depth > 0) {
    const char = text.charAt(pos);

    if (inString) {
      if (char === stringChar && text.charAt(pos - 1) !== '\\') {
        inString = false;
      }
    } else {
      if (char === '"' || char === "'") {
        inString = true;
        stringChar = char;
      } else if (char === '(') {
        depth++;
      } else if (char === ')') {
        depth--;
      }
    }
    pos++;
  }

  return depth === 0 ? pos : -1;
}

/** Placeholder shown when an expression can't be evaluated without a run. */
const UNRESOLVED_PLACEHOLDER = '[Run workflow for preview]';

/** Find the index of the `)` matching the `(` at `openIdx`, skipping string literals. */
function findMatchingParen(s: string, openIdx: number): number {
  let depth = 0;
  let inString = false;
  let stringChar = '';
  for (let i = openIdx; i < s.length; i++) {
    const ch = s.charAt(i);
    if (inString) {
      if (ch === stringChar && s.charAt(i - 1) !== '\\') inString = false;
      continue;
    }
    if (ch === '"' || ch === "'") { inString = true; stringChar = ch; continue; }
    if (ch === '(') depth++;
    else if (ch === ')' && --depth === 0) return i;
  }
  return -1;
}

/** Strip `${ }`, `@{ }`, or leading `@` wrapper from an expression segment. */
function stripExpressionWrapper(segText: string): string {
  if ((segText.startsWith('${') || segText.startsWith('@{')) && segText.endsWith('}')) {
    return segText.slice(2, -1);
  }
  if (segText.startsWith('@')) return segText.slice(1);
  return segText;
}

/**
 * Split a loop-function expression so the surrounding logic stays readable
 * (e.g. `?.status === 200 || ...`) and only the unresolvable calls are marked
 * placeholder. Returns null on malformed parens so callers can fall back.
 */
function splitLoopExpression(segText: string): PreviewSegment[] | null {
  const body = stripExpressionWrapper(segText);
  const loopCallRe = /\b(?:item|items|iterationIndexes)\s*\(/g;
  const segments: PreviewSegment[] = [];
  let lastIdx = 0;
  let match: RegExpExecArray | null;

  while ((match = loopCallRe.exec(body)) !== null) {
    const callStart = match.index;
    const closeIdx = findMatchingParen(body, callStart + match[0].length - 1);
    if (closeIdx < 0) return null;
    if (callStart > lastIdx) {
      segments.push({ text: body.slice(lastIdx, callStart), type: 'literal' });
    }
    segments.push({ text: body.slice(callStart, closeIdx + 1), type: 'unresolved' });
    lastIdx = closeIdx + 1;
    loopCallRe.lastIndex = lastIdx;
  }

  if (lastIdx < body.length) {
    segments.push({ text: body.slice(lastIdx), type: 'literal' });
  }
  return segments;
}

/**
 * Generate preview segments — synchronously parses text, then evaluates
 * expression segments via the worker (async). Returns a promise.
 */
async function generatePreviewSegmentsAsync(
  text: string,
  actionOutputs?: Record<string, unknown>,
  parameters?: Record<string, unknown>
): Promise<{ segments: PreviewSegment[]; hasUnresolvedExpressions: boolean; hasErrors: boolean; hasAgentParameters: boolean }> {
  if (!text) {
    return { segments: [], hasUnresolvedExpressions: false, hasErrors: false, hasAgentParameters: false };
  }

  const parsedSegments = parseExpressions(text);
  if (parsedSegments.length === 0) {
    return { segments: [{ text, type: 'literal' }], hasUnresolvedExpressions: false, hasErrors: false, hasAgentParameters: false };
  }

  const variables = (actionOutputs?.['__variables__'] ?? {}) as Record<string, unknown>;
  const evalContext: EvaluationContext = {
    trigger: actionOutputs?.['trigger'],
    actionOutputs: actionOutputs ?? {},
    variables,
    parameters: parameters ?? {},
  };

  let hasUnresolvedExpressions = false;
  let hasErrors = false;
  let hasAgentParameters = false;
  const previewSegments: PreviewSegment[] = [];

  // Collect expression segments that need worker evaluation
  const toEvaluate: { index: number; text: string }[] = [];

  for (let i = 0; i < parsedSegments.length; i++) {
    const seg = parsedSegments[i]!;
    if (!seg.isExpression) {
      previewSegments.push({ text: seg.text, type: 'literal' });
      continue;
    }

    const usesLoopFunctions = LOOP_FUNCTION_PATTERN.test(seg.text);
    const usesAgentParameters = /agentparameters\s*\(/i.test(seg.text);
    const needsActionData = /body\s*\(|outputs\s*\(|actions\s*\(|trigger/.test(seg.text);
    const needsVariableData = /variables\s*\(/.test(seg.text);

    if (usesLoopFunctions) {
      const loopSegs = splitLoopExpression(seg.text);
      if (loopSegs) { previewSegments.push(...loopSegs); hasUnresolvedExpressions = true; }
      else { previewSegments.push({ text: UNRESOLVED_PLACEHOLDER, type: 'unresolved' }); hasUnresolvedExpressions = true; }
      continue;
    }

    if (usesAgentParameters) {
      const paramMatch = seg.text.match(/agentparameters\s*\(\s*['"]([^'"]+)['"]\s*\)/i);
      previewSegments.push({ text: `[Agent: ${paramMatch ? paramMatch[1] : 'value'}]`, type: 'unresolved' });
      hasAgentParameters = true;
      continue;
    }

    if (needsActionData && !actionOutputs) {
      previewSegments.push({ text: UNRESOLVED_PLACEHOLDER, type: 'unresolved' }); hasUnresolvedExpressions = true;
      continue;
    }

    if (needsVariableData && Object.keys(variables).length === 0) {
      previewSegments.push({ text: UNRESOLVED_PLACEHOLDER, type: 'unresolved' }); hasUnresolvedExpressions = true;
      continue;
    }

    // Mark position and collect for batch evaluation
    toEvaluate.push({ index: previewSegments.length, text: seg.text });
    previewSegments.push({ text: '', type: 'literal' }); // placeholder
  }

  // Batch evaluate all expression segments in one worker round-trip
  if (toEvaluate.length > 0) {
    const results = await evaluateBatchAsync(toEvaluate.map((e) => e.text), evalContext);
    for (let i = 0; i < toEvaluate.length; i++) {
      const result = results[i]!;
      const idx = toEvaluate[i]!.index;
      if (result.success) {
        previewSegments[idx] = { text: formatResolvedValue(result.value, Infinity), type: 'resolved' };
      } else {
        const err = result.error ?? '';
        if (err.includes('undefined') || err.includes('not found') || err.includes('is not defined')) {
          previewSegments[idx] = { text: UNRESOLVED_PLACEHOLDER, type: 'unresolved' };
          hasUnresolvedExpressions = true;
        } else {
          previewSegments[idx] = { text: `Error: ${err}`, type: 'error' };
          hasErrors = true;
        }
      }
    }
  }

  return { segments: previewSegments, hasUnresolvedExpressions, hasErrors, hasAgentParameters };
}

export interface ExpressionPreviewProps {
  /** The value to preview - can be string, object, or array */
  value: unknown;
  /** Action outputs for expression evaluation */
  actionOutputs?: Record<string, unknown> | undefined;
  /** Whether we have run data available */
  hasRunData?: boolean | undefined;
  /** Whether the workflow has changed since the last run */
  isStale?: boolean | undefined;
  /** Label for the preview section */
  label?: string | undefined;
  /**
   * Display mode:
   * - 'inline' (default): Shows as part of document flow, for composite editors like DictionaryField
   * - 'overlay': Shows as absolute-positioned overlay below parent, for simple fields like StringField
   */
  mode?: 'inline' | 'overlay' | undefined;
  /** Event handlers for overlay mode to track user interaction (for preventing blur) */
  onMouseDown?: (() => void) | undefined;
  onMouseUp?: (() => void) | undefined;
  onMouseLeave?: (() => void) | undefined;
}

export const ExpressionPreview = ({
  value,
  actionOutputs,
  hasRunData = false,
  isStale = false,
  label = 'Preview',
  mode = 'inline',
  onMouseDown,
  onMouseUp,
  onMouseLeave,
}: ExpressionPreviewProps) => {
  const styles = useStyles();
  const isOverlay = mode === 'overlay';

  // Resolved `@parameters('name')` values for the evaluation context.
  const parameterValues = useParameterValues();

  // Preview visibility — user-triggered. Closed by default.
  const [isOpen, setIsOpen] = useState(false);
  const toggleOpen = useCallback(() => setIsOpen((v) => !v), []);

  // Focus management: when the preview opens, the trigger button unmounts and is
  // replaced by the panel, so keyboard focus would otherwise fall to <body>. Move
  // focus into the panel (Close button) on open and restore it to the Preview
  // trigger on close. Only act on actual open/close transitions — never on the
  // initial mount (which would steal focus on first render).
  //
  // Note: these are passed to Fluent <Button> via `as unknown as React.Ref<never>`
  // at the JSX sites — Fluent's polymorphic ForwardRefComponent types its ref as a
  // union (button|anchor|...) that TS collapses to `never`, so the cast is required
  // to attach a concrete HTMLButtonElement ref. This is the established pattern in
  // this codebase (see FloatingAgentNode.tsx).
  const previewButtonRef = useRef<HTMLButtonElement>(null);
  const closeButtonRef = useRef<HTMLButtonElement>(null);
  const prevOpenRef = useRef(isOpen);
  useEffect(() => {
    const wasOpen = prevOpenRef.current;
    prevOpenRef.current = isOpen;
    if (isOpen && !wasOpen) {
      closeButtonRef.current?.focus();
    } else if (!isOpen && wasOpen) {
      previewButtonRef.current?.focus();
    }
  }, [isOpen]);

  // State for toggling between code view and HTML preview
  const [showHtmlPreview, setShowHtmlPreview] = useState(true);

  // Async evaluation state
  const [segments, setSegments] = useState<PreviewSegment[]>([]);
  const [evalMeta, setEvalMeta] = useState({ hasUnresolvedExpressions: false, hasErrors: false, hasAgentParameters: false });
  const [loading, setLoading] = useState(false);
  const evalGeneration = useRef(0);

  // Convert value to string for display and check for expressions
  const { displayText, hasExpressions, hasJsExpressions, hasHtmlTags, isJsonLiteral } = useMemo(() => {
    if (value === null || value === undefined) {
      return { displayText: '', hasExpressions: false, hasJsExpressions: false, hasHtmlTags: false, isJsonLiteral: false };
    }

    let text: string;
    let jsonLiteral = false;
    if (typeof value === 'string') {
      text = value;
    } else {
      text = JSON.stringify(value, null, 2);
      jsonLiteral = true;
    }

    return {
      displayText: text,
      hasExpressions: containsExpression(text),
      hasJsExpressions: containsJavaScriptExpression(text),
      hasHtmlTags: containsHtmlTags(text),
      isJsonLiteral: jsonLiteral,
    };
  }, [value]);

  // Evaluate expressions only when the preview is open
  useEffect(() => {
    if (!isOpen || !displayText || !hasExpressions) {
      setSegments(displayText ? [{ text: displayText, type: 'literal' }] : []);
      setEvalMeta({ hasUnresolvedExpressions: false, hasErrors: false, hasAgentParameters: false });
      setLoading(false);
      return;
    }

    const gen = ++evalGeneration.current;
    setLoading(true);

    generatePreviewSegmentsAsync(displayText, actionOutputs, parameterValues)
      .then((result) => {
        if (gen !== evalGeneration.current) return; // stale
        setSegments(result.segments);
        setEvalMeta({
          hasUnresolvedExpressions: result.hasUnresolvedExpressions,
          hasErrors: result.hasErrors,
          hasAgentParameters: result.hasAgentParameters,
        });
        setLoading(false);
      })
      .catch((err) => {
        if (gen !== evalGeneration.current) return; // stale
        setSegments([{ text: `Preview failed: ${err instanceof Error ? err.message : String(err)}`, type: 'error' }]);
        setEvalMeta({ hasUnresolvedExpressions: false, hasErrors: true, hasAgentParameters: false });
        setLoading(false);
      });
  }, [isOpen, displayText, hasExpressions, actionOutputs, parameterValues]);

  // Generate evaluated HTML string for HTML preview mode.
  // The literal segments contain user-authored markup, so the assembled string is run
  // through DOMPurify (sanitizeHtml) before it reaches dangerouslySetInnerHTML — this strips
  // scripts/inline event handlers while preserving our resolved/unresolved/error marker spans.
  const evaluatedHtml = useMemo(() => {
    if (!hasHtmlTags || segments.length === 0) return '';
    const assembled = segments
      .map((segment) => {
        if (segment.type === 'literal') return segment.text;
        if (segment.type === 'resolved') {
          const escaped = segment.text.replace(/&/g, '&amp;').replace(/</g, '&lt;').replace(/>/g, '&gt;');
          return `<span class="resolved-value">${escaped}</span>`;
        }
        if (segment.type === 'unresolved') return `<span class="unresolved-value">${segment.text}</span>`;
        if (segment.type === 'error') return `<span class="error-value">${segment.text}</span>`;
        return segment.text;
      })
      .join('');
    return sanitizeHtml(assembled);
  }, [hasHtmlTags, segments]);

  // Don't render anything if there's no expression content
  if (!displayText || !hasExpressions) {
    return null;
  }

  const canShowHtmlPreview = hasHtmlTags;

  // Tip messages
  const { hasUnresolvedExpressions, hasErrors, hasAgentParameters } = evalMeta;
  const showStaleTip = isStale && hasRunData;
  const showNoRunDataTip = !hasRunData && hasUnresolvedExpressions && !hasAgentParameters;
  const showUnresolvedTip = hasRunData && !isStale && hasUnresolvedExpressions && !hasAgentParameters;
  const showAgentParameterTip = hasAgentParameters;
  const showErrorTip = hasErrors;
  const showJavaScriptTip =
    !showStaleTip && !showNoRunDataTip && !showUnresolvedTip && !showAgentParameterTip && !showErrorTip;

  const tipMessages: string[] = [
    showStaleTip &&
      'Preview shows data from the last successful run. The workflow has changed - run it again to refresh.',
    showNoRunDataTip && 'Run the workflow to see preview values from actual execution data.',
    showUnresolvedTip && "Some expressions reference actions that haven't run yet.",
    showAgentParameterTip && 'Agent parameters are provided by the agent at runtime.',
    showErrorTip && 'Some expressions have errors. Check the syntax.',
    showJavaScriptTip &&
      (hasJsExpressions
        ? 'Tip: Type . after a value for method suggestions.'
        : 'Tip: Anything inside ${ } is JavaScript.'),
  ].filter((m): m is string => typeof m === 'string');

  // JSON token style map
  const jsonTokenStyleMap: Partial<Record<JsonTokenKind, string>> = {
    key: styles.jsonKey,
    string: styles.jsonString,
    number: styles.jsonNumber,
    boolean: styles.jsonBoolean,
    null: styles.jsonNull,
    punctuation: styles.jsonPunctuation,
  };

  // When closed, render just the toggle button
  if (!isOpen) {
    return (
      <div className={isOverlay ? styles.containerOverlay : styles.container}>
        <Tooltip content="Evaluate expressions" relationship="description">
          <Button
            /* Fluent ForwardRef union -> never; cast to attach concrete ref */
            ref={previewButtonRef as unknown as React.Ref<never>}
            className={styles.previewToggleButton}
            size="small"
            appearance="subtle"
            icon={<EyeRegular />}
            onClick={toggleOpen}
            aria-label="Show expression preview"
          >
            Preview
          </Button>
        </Tooltip>
      </div>
    );
  }

  return (
    <div
      className={isOverlay ? styles.containerOverlay : styles.container}
      onMouseDown={onMouseDown}
      onMouseUp={onMouseUp}
      onMouseLeave={onMouseLeave}
    >
      <div className={mergeClasses(styles.panel, isOverlay && styles.panelOverlay)}>
        <div className={styles.headerWithActions}>
          <Text className={styles.label}>{label}</Text>
          <div className={styles.headerActions}>
            {canShowHtmlPreview && (
              <Tooltip
                content={showHtmlPreview ? 'Show code view' : 'Show HTML preview'}
                relationship="description"
              >
                <ToggleButton
                  className={styles.toggleButton}
                  size="small"
                  appearance="subtle"
                  checked={showHtmlPreview}
                  icon={showHtmlPreview ? <EyeRegular /> : <CodeRegular />}
                  onClick={() => setShowHtmlPreview(!showHtmlPreview)}
                  aria-label={showHtmlPreview ? 'Show code view' : 'Show HTML preview'}
                />
              </Tooltip>
            )}
            <Tooltip content="Close preview" relationship="description">
              <Button
                /* Fluent ForwardRef union -> never; cast to attach concrete ref */
                ref={closeButtonRef as unknown as React.Ref<never>}
                className={styles.toggleButton}
                size="small"
                appearance="subtle"
                icon={<EyeOffRegular />}
                onClick={toggleOpen}
                aria-label="Close preview"
              />
            </Tooltip>
          </div>
        </div>

        {loading ? (
          <div className={styles.loadingContainer}>
            <Spinner size="tiny" label="Evaluating..." labelPosition="after" />
          </div>
        ) : canShowHtmlPreview && showHtmlPreview ? (
          /* HTML Preview Mode */
          <div
            className={styles.htmlPreviewContainer}
            dangerouslySetInnerHTML={{ __html: evaluatedHtml }}
          />
        ) : (
          /* Code/Text View */
          <div className={styles.codeBlock}>
            <div className={styles.content}>
              {segments.map((segment, index) => {
                if (segment.type === 'resolved') {
                  return <span key={index} className={styles.resolvedValue}>{segment.text}</span>;
                }
                if (segment.type === 'unresolved') {
                  return <span key={index} className={styles.placeholder}>{segment.text}</span>;
                }
                if (segment.type === 'error') {
                  return <span key={index} className={styles.error}>{segment.text}</span>;
                }
                if (isJsonLiteral) {
                  const tokensForSegment = tokenizeJson(segment.text);
                  return (
                    <span key={index}>
                      {tokensForSegment.map((tok, tIndex) => {
                        const className = jsonTokenStyleMap[tok.kind];
                        return className
                          ? <span key={tIndex} className={className}>{tok.text}</span>
                          : <span key={tIndex}>{tok.text}</span>;
                      })}
                    </span>
                  );
                }
                return <span key={index}>{segment.text}</span>;
              })}
            </div>
          </div>
        )}

        {/* Contextual tips */}
        {!loading &&
          tipMessages.map((message, i) => (
            <div key={i} className={styles.tip}>
              <InfoRegular className={styles.tipIcon} fontSize={14} />
              <Text size={200} className={styles.tipText}>{message}</Text>
            </div>
          ))}
      </div>
    </div>
  );
};

ExpressionPreview.displayName = 'ExpressionPreview';
